import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { Observable, catchError, throwError } from 'rxjs';
import { SnackBarService } from '../snackbar/snackbar-service';
import { SnackbarTypes } from '../snackbar/snackbar-types';
import { TranslocoService } from '@jsverse/transloco';
import { ErrorHandlingService } from '../error-handling/error-handling.service';
import { ApiErrorResponse } from '../models/error-response.interface';

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const _snackBarService = inject(SnackBarService);
    const _translocoService = inject(TranslocoService);
    const _errorHandlingService = inject(ErrorHandlingService);
    // Clone the request object
    let newReq = req.clone();

    // Request
    //
    // If the access token didn't expire, add the Authorization header.
    // We won't add the Authorization header if the access token expired.
    // This will force the server to return a "401 Unauthorized" response
    // for the protected API routes which our response interceptor will
    // catch and delete the access token from the local storage while logging
    // the user out from the app.
    if (
        authService.accessToken
    ) {
        newReq = req.clone({
            headers: req.headers.set(
                'Authorization',
                'Bearer ' + authService.accessToken
            ),
        });
    }

    // Response
    return next(newReq).pipe(
        catchError((error: HttpErrorResponse) => {
            const apiError = error.error as ApiErrorResponse;

            switch (error.status) {
                case 400: {
                    // For inventory errors (E039), use the detailed message from the backend
                    // Otherwise, use error handling service for better error message resolution
                    let errorMessage: string;

                    if (apiError?.errorKey === 'E039' && apiError?.message) {
                        // Use the detailed message from backend that includes product name and quantities
                        errorMessage = apiError.message;
                    } else {
                        errorMessage = _errorHandlingService.getErrorMessage(
                            apiError?.errorKey || apiError?.message,
                            apiError?.detail
                        );
                    }

                    showSnackBar({
                        message: errorMessage,
                        snackBarService: _snackBarService,
                        disableTranslate: true
                    });
                    break;
                }
                case 401: {
                    showSnackBar({
                        message: _errorHandlingService.getErrorMessage('sessionExpired'),
                        snackBarService: _snackBarService,
                        disableTranslate: true
                    });
                    authService.signOut();
                    break;
                }
                case 403: {
                    showSnackBar({
                        message: _translocoService.translate('errors.forbidden'),
                        snackBarService: _snackBarService,
                        disableTranslate: true
                    });
                    break;
                }

                case 404: {
                    showSnackBar({
                        message: _errorHandlingService.getErrorMessage('notFound'),
                        snackBarService: _snackBarService,
                        disableTranslate: true
                    });
                    break;
                }

                case 500: {
                    showSnackBar({
                        message: _errorHandlingService.getErrorMessage(
                            apiError?.errorKey,
                            _translocoService.translate('errors.internalServer')
                        ),
                        snackBarService: _snackBarService,
                        disableTranslate: true
                    });
                    break;
                }

                default: {
                    showSnackBar({
                        message: _errorHandlingService.getErrorMessage('unexpected'),
                        snackBarService: _snackBarService,
                        disableTranslate: true
                    });
                    break;
                }
            }


            return throwError(error);
        })
    );
};


const showSnackBar = ({ snackBarService, message, snackbarType, disableTranslate }: {
    snackBarService: SnackBarService,
    message: string,
    snackbarType?: SnackbarTypes,
    disableTranslate?: boolean
}) => {
    snackBarService.showSnackBar({
        message,
        snackBarType: snackbarType ?? SnackbarTypes.errorMessage,
        disableTranslate: disableTranslate ?? false
    });
}